import { BackendCopilotAgentWidgetTypes } from '@prophecy/shared-core/agentChat/widgetTypes';
import { applyJsonPatch } from '@prophecy/utils/data';
import produce from 'immer';

export type HomeChatRole = 'user' | 'assistant' | 'system';

export type HomeChatElement = {
  id: string;
  role?: HomeChatRole;
  type: string;
  properties?: Record<string, unknown>;
  metainfo?: { startTime?: number; lastUpdateTime?: number; endTime?: number };
};

export type HomeChatMessages = Record<string, HomeChatElement>;

export type ChatPatchPayload = {
  path: string;
  value: unknown;
  update_type: 'write' | 'append';
};

export type HomeChatTurn = {
  id: string;
  user?: HomeChatElement;
  steps: HomeChatElement[];
  answer?: HomeChatElement;
};

export const HOME_CHAT_ELEMENT_TYPES = {
  Markdown: 'Markdown',
  Error: 'Error',
  UserInputRequest: 'user_input_request',
  AgentMessage: BackendCopilotAgentWidgetTypes.AgentMessage,
  AgentThinking: BackendCopilotAgentWidgetTypes.AgentThinking,
  ToolUse: BackendCopilotAgentWidgetTypes.ToolUse,
  FinalMessage: BackendCopilotAgentWidgetTypes.FinalMessage
} as const;

const MESSAGES_ROOT = '$.chat.messages';

function isElement(value: unknown): value is HomeChatElement {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as HomeChatElement).id === 'string'
  );
}

/** Mirrors the backend's JSONL reader: later lines with the same id replace earlier ones. */
export function parseTranscriptJsonl(content: string | null | undefined): HomeChatMessages {
  const messages: HomeChatMessages = {};
  if (!content) return messages;
  for (const line of content.split('\n')) {
    if (!line.trim()) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(line);
    } catch {
      continue;
    }
    if (isElement(parsed)) messages[parsed.id] = parsed;
  }
  return messages;
}

/** Later layers win, so pass oldest first and live updates last. */
export function mergeTranscripts(layers: HomeChatMessages[]): HomeChatMessages {
  return Object.assign({}, ...layers);
}

/**
 * Applies one `copilot/chat/didUpdate` payload, the way the project chat's `applyPatch` does.
 * Returns null for a path this chat does not own, so the caller can drop it.
 */
export function applyChatPatch(messages: HomeChatMessages, payload: ChatPatchPayload): HomeChatMessages | null {
  if (payload.path !== MESSAGES_ROOT && !payload.path.startsWith(`${MESSAGES_ROOT}.`)) return null;
  const relativePath = payload.path.slice(MESSAGES_ROOT.length + 1);
  if (!relativePath) return null;

  return produce(messages, (draft) => {
    applyJsonPatch(draft, relativePath, payload.value, payload.update_type);
  });
}

/**
 * Patches the live layer that sits over the loaded history. A nested patch on an element only the
 * history holds copies that element up first, or the live fragment would shadow it.
 */
export function applyLivePatch(
  live: HomeChatMessages,
  history: HomeChatMessages,
  payload: ChatPatchPayload
): HomeChatMessages | null {
  const [elementId, ...field] = payload.path.slice(MESSAGES_ROOT.length + 1).split('.');
  const seeded =
    field.length > 0 && elementId && !live[elementId] && history[elementId]
      ? { ...live, [elementId]: history[elementId] }
      : live;
  return applyChatPatch(seeded, payload);
}

// The sandbox also draws the AskUserQuestion call as a tool card; its question is the request card.
const ASK_USER_QUESTION_TOOL = 'AskUserQuestion';

function isHidden(element: HomeChatElement): boolean {
  if (element.type === HOME_CHAT_ELEMENT_TYPES.FinalMessage) return true;
  return element.type === HOME_CHAT_ELEMENT_TYPES.ToolUse && element.properties?.tool_type === ASK_USER_QUESTION_TOOL;
}

function isPendingRequest(element: HomeChatElement): boolean {
  return element.type === HOME_CHAT_ELEMENT_TYPES.UserInputRequest && element.properties?.answered !== true;
}

/** Questions and permission asks still waiting on the user, oldest first. */
export function pendingRequests(messages: HomeChatMessages): HomeChatElement[] {
  return Object.values(messages)
    .filter((element) => isElement(element) && isPendingRequest(element))
    .sort((a, b) => (a.metainfo?.startTime ?? 0) - (b.metainfo?.startTime ?? 0));
}

/**
 * A start time for a prompt typed now, on the server's clock: turns are ordered by `startTime`,
 * and the agent stamps its elements with the server's clock, not the browser's.
 */
export function nextPromptStartTime(messages: HomeChatMessages): number {
  let latest = 0;
  for (const element of Object.values(messages)) {
    const { startTime = 0, lastUpdateTime = 0, endTime = 0 } = element.metainfo ?? {};
    latest = Math.max(latest, startTime, lastUpdateTime, endTime);
  }
  return latest + 1;
}

/** True while the latest element, in `buildTurns` order, is a prompt nothing has come back for. */
export function lastPromptAwaitsReply(messages: HomeChatMessages): boolean {
  let latest: HomeChatElement | undefined;
  for (const element of Object.values(messages)) {
    if (!isElement(element)) continue;
    if (!latest || (element.metainfo?.startTime ?? 0) >= (latest.metainfo?.startTime ?? 0)) latest = element;
  }
  return latest?.role === 'user';
}

function newTurn(id: string, user?: HomeChatElement): HomeChatTurn {
  return { id, user, steps: [] };
}

/** Groups elements into prompt turns; a turn's trailing assistant message is its answer. */
export function buildTurns(messages: HomeChatMessages): HomeChatTurn[] {
  const ordered = Object.values(messages)
    .filter(isElement)
    .map((element, index) => ({ element, index }))
    .sort((a, b) => (a.element.metainfo?.startTime ?? 0) - (b.element.metainfo?.startTime ?? 0) || a.index - b.index)
    .map(({ element }) => element);

  const turns: HomeChatTurn[] = [];
  let current: HomeChatTurn | undefined;

  for (const element of ordered) {
    if (element.role === 'user') {
      current = newTurn(element.id, element);
      turns.push(current);
      continue;
    }
    if (isHidden(element)) continue;
    if (!current) {
      current = newTurn(`leading-${element.id}`);
      turns.push(current);
    }
    if (!isPendingRequest(element)) current.steps.push(element);
  }

  for (const turn of turns) {
    const last = turn.steps[turn.steps.length - 1];
    if (last?.type === HOME_CHAT_ELEMENT_TYPES.AgentMessage) {
      turn.answer = last;
      turn.steps = turn.steps.slice(0, -1);
    }
  }
  return turns;
}
